// src/finance/engine/invert.ts
// Sens inverse (spec § 1.1) : net(CA) est strictement croissante dès que CA > dépenses
// (taux marginaux toujours < 100 %), donc une recherche binaire converge — pas
// d'inversion analytique (barème progressif + marginal relief la rendraient fragile).
import type { CorporateRegime, FinanceSettings } from './types'
import { simulateRegime } from './engine'
import { round2 } from './tax'

const TOLERANCE = 0.005
const MAX_ITERATIONS = 200

/**
 * CA mensuel produisant le net visé, ou `null` si la cible est INATTEIGNABLE :
 * quand la phase de doublement épuise ses itérations sans dépasser la cible
 * (paramètres confiscatoires — PFU à 100 %, cotisations > 100 %…), la bissection
 * n'a pas d'encadrement valide et rendait un nombre absurde (~1e21). Les appelants
 * doivent traiter `null` comme « cible inatteignable avec ces paramètres ».
 */
export function findRevenueForNet(
  targetNetMonthly: number,
  share: number,
  settings: FinanceSettings,
  companyExpensesMonthly: number,
  corporateRegime: CorporateRegime = 'uk',
): number | null {
  if (targetNetMonthly <= 0) return round2(companyExpensesMonthly)

  const netAt = (revenue: number): number =>
    simulateRegime(revenue, share, settings, companyExpensesMonthly, corporateRegime).netMonthly

  // Borne haute : double jusqu'à dépasser la cible.
  let lo = companyExpensesMonthly
  let hi = Math.max(companyExpensesMonthly + targetNetMonthly, 1_000)
  let guard = 0
  while (netAt(hi) < targetNetMonthly && guard < 60) {
    hi *= 2
    guard += 1
  }
  // Doublements épuisés sans jamais atteindre la cible : pas d'encadrement, donc
  // pas de bissection possible — la cible est hors de portée de ces paramètres.
  if (netAt(hi) < targetNetMonthly) return null

  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    const mid = (lo + hi) / 2
    const net = netAt(mid)
    if (Math.abs(net - targetNetMonthly) <= TOLERANCE) return round2(mid)
    if (net < targetNetMonthly) lo = mid
    else hi = mid
  }
  return round2((lo + hi) / 2)
}
